import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

const WA = API_URL.WHATSAPP;

export const getWhatsAppConfig = createAsyncThunk(
  "whatsAppConfig/get",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(WA.GET);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const connectWhatsAppManual = createAsyncThunk(
  "whatsAppConfig/connectManual",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(WA.CONNECT_MANUAL, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Embedded Signup (WhatsApp Business app coexistence): exchange the short-lived
// auth code from Meta's popup for a server-side business token. The server
// derives WABA/phone identity from the token (never trusts client-supplied
// ids), verifies the number is a WhatsApp Business app number, subscribes the
// app, requests Meta's contact/history sync and syncs templates. It never
// registers the number. Payload: code + session-info hints (event, ids).
export const exchangeWhatsAppSignup = createAsyncThunk(
  "whatsAppConfig/exchange",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await axios.post(WA.EXCHANGE, payload);
      dispatch(getWhatsAppConfig());
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateWhatsAppSettings = createAsyncThunk(
  "whatsAppConfig/updateSettings",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      await axios.put(WA.UPDATE_SETTINGS, payload);
      dispatch(getWhatsAppConfig());
      return true;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const testWhatsAppConnection = createAsyncThunk(
  "whatsAppConfig/testConnection",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(WA.TEST_CONNECTION);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const syncWhatsAppTemplates = createAsyncThunk(
  "whatsAppConfig/syncTemplates",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await axios.post(WA.SYNC_TEMPLATES);
      dispatch(getWhatsAppTemplates());
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const disconnectWhatsApp = createAsyncThunk(
  "whatsAppConfig/disconnect",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await axios.post(WA.DISCONNECT);
      dispatch(getWhatsAppConfig());
      return true;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getWhatsAppTemplates = createAsyncThunk(
  "whatsAppConfig/getTemplates",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(WA.TEMPLATES);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getWhatsAppEventConfigs = createAsyncThunk(
  "whatsAppConfig/getEventConfigs",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(WA.EVENT_CONFIGS);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const saveWhatsAppEventConfig = createAsyncThunk(
  "whatsAppConfig/saveEventConfig",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      await axios.put(WA.SAVE_EVENT_CONFIG, payload);
      dispatch(getWhatsAppEventConfigs());
      return true;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Manual "Send on WhatsApp" — funnels through the SAME backend dispatch entry
// point as Auto (so the two paths can't drift). Payload is ids only
// { eventKey, entityType, entityId }; the server resolves the recipient,
// renders the template and returns the resulting status/reason.
export const sendWhatsAppMessage = createAsyncThunk(
  "whatsAppConfig/sendMessage",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(WA.SEND, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getWhatsAppMessageLog = createAsyncThunk(
  "whatsAppConfig/getMessageLog",
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(WA.MESSAGE_LOG, { params });
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const initialState = {
  config: null,
  templates: [],
  eventConfigs: [],
  log: null,
  isLoading: false,
  isConnecting: false,
  isSaving: false,
  isTesting: false,
  isSyncing: false,
  isLogLoading: false,
  isSendingMessage: false,
  testResult: null,
  syncResult: null,
  sendResult: null,
};

const whatsAppConfigSlice = createSlice({
  name: "whatsAppConfig",
  initialState,
  reducers: {
    clearWhatsAppTransient(state) {
      state.testResult = null;
      state.syncResult = null;
      state.sendResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWhatsAppConfig.pending, (s) => {
        s.isLoading = true;
      })
      .addCase(getWhatsAppConfig.fulfilled, (s, a) => {
        s.isLoading = false;
        s.config = a.payload;
      })
      .addCase(getWhatsAppConfig.rejected, (s) => {
        s.isLoading = false;
      })

      .addCase(connectWhatsAppManual.pending, (s) => {
        s.isConnecting = true;
      })
      .addCase(connectWhatsAppManual.fulfilled, (s, a) => {
        s.isConnecting = false;
        s.config = a.payload;
      })
      .addCase(connectWhatsAppManual.rejected, (s) => {
        s.isConnecting = false;
      })

      .addCase(exchangeWhatsAppSignup.pending, (s) => {
        s.isConnecting = true;
      })
      .addCase(exchangeWhatsAppSignup.fulfilled, (s, a) => {
        s.isConnecting = false;
        if (a.payload) s.config = a.payload;
      })
      .addCase(exchangeWhatsAppSignup.rejected, (s) => {
        s.isConnecting = false;
      })

      .addCase(updateWhatsAppSettings.pending, (s) => {
        s.isSaving = true;
      })
      .addCase(updateWhatsAppSettings.fulfilled, (s) => {
        s.isSaving = false;
      })
      .addCase(updateWhatsAppSettings.rejected, (s) => {
        s.isSaving = false;
      })

      .addCase(testWhatsAppConnection.pending, (s) => {
        s.isTesting = true;
        s.testResult = null;
      })
      .addCase(testWhatsAppConnection.fulfilled, (s, a) => {
        s.isTesting = false;
        s.testResult = a.payload;
      })
      .addCase(testWhatsAppConnection.rejected, (s) => {
        s.isTesting = false;
        s.testResult = { success: false, message: "Connection test failed" };
      })

      .addCase(syncWhatsAppTemplates.pending, (s) => {
        s.isSyncing = true;
        s.syncResult = null;
      })
      .addCase(syncWhatsAppTemplates.fulfilled, (s, a) => {
        s.isSyncing = false;
        s.syncResult = a.payload;
      })
      .addCase(syncWhatsAppTemplates.rejected, (s) => {
        s.isSyncing = false;
        s.syncResult = { success: false, message: "Template sync failed" };
      })

      .addCase(getWhatsAppTemplates.fulfilled, (s, a) => {
        s.templates = a.payload || [];
      })

      .addCase(getWhatsAppEventConfigs.pending, (s) => {
        s.isLoading = true;
      })
      .addCase(getWhatsAppEventConfigs.fulfilled, (s, a) => {
        s.isLoading = false;
        s.eventConfigs = a.payload || [];
      })
      .addCase(getWhatsAppEventConfigs.rejected, (s) => {
        s.isLoading = false;
      })

      .addCase(saveWhatsAppEventConfig.pending, (s) => {
        s.isSaving = true;
      })
      .addCase(saveWhatsAppEventConfig.fulfilled, (s) => {
        s.isSaving = false;
      })
      .addCase(saveWhatsAppEventConfig.rejected, (s) => {
        s.isSaving = false;
      })

      .addCase(sendWhatsAppMessage.pending, (s) => {
        s.isSendingMessage = true;
        s.sendResult = null;
      })
      .addCase(sendWhatsAppMessage.fulfilled, (s, a) => {
        s.isSendingMessage = false;
        s.sendResult = a.payload;
      })
      .addCase(sendWhatsAppMessage.rejected, (s) => {
        s.isSendingMessage = false;
      })

      .addCase(getWhatsAppMessageLog.pending, (s) => {
        s.isLogLoading = true;
      })
      .addCase(getWhatsAppMessageLog.fulfilled, (s, a) => {
        s.isLogLoading = false;
        s.log = a.payload;
      })
      .addCase(getWhatsAppMessageLog.rejected, (s) => {
        s.isLogLoading = false;
      });
  },
});

export const { clearWhatsAppTransient } = whatsAppConfigSlice.actions;
export default whatsAppConfigSlice.reducer;
